/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { later } from '@ember/runloop';
import { tablePrintCss } from 'linced-app/utils/table';
import {
  cleanExcelSheetName,
  cleanFileName,
  uniqueSheetName,
} from '@linc-technologies/linced-common/utils/file-names';
import GroupTypes from '@linc-technologies/linced-common/enums/group-type';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import titleize from 'ember-cli-string-helpers/utils/titleize';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ClassRollController extends Controller {
  queryParams = [
    'teamId',
    'reliever',
    'weekly',
    'classId',
    'date',
    'fullClass',
  ];

  // Services
  @service
  dates;

  @service
  excel;

  @service
  options;

  @service
  session;

  @service
  store;

  GroupTypes = GroupTypes;
  SchoolStatus = SchoolStatus;
  EnrolmentStatus = EnrolmentStatus;
  @optionAlias('core:groups:teams')
  teams;

  @filterBy('teams', 'value')
  cleanTeams;

  @tracked teamId = null;
  @tracked classId = null;
  @tracked reliever = false;
  @tracked weekly = false;
  @tracked fullClass = false;
  @tracked date = moment().format('YYYY-MM-DD');
  minDate = moment().format('YYYY-01-01');
  maxDate = moment().format('YYYY-12-31');

  @computed('teamId', 'cleanTeams.[]')
  get team() {
    let team = this.cleanTeams.find((item) => item.value === this.teamId);
    return !isEmpty(team) ? team : null;
  }

  get homerooms() {
    if (this.fullClass) {
      return this.model.groups;
    }
    if (!isEmpty(this.teamId)) {
      return this.model.groups.filter((group) => group.team === this.teamId);
    }

    if (isEmpty(this.classId)) {
      return [];
    }

    let homeroom = this.model.groups.find((group) => group.id === this.classId);

    if (isEmpty(homeroom)) {
      // Group doesn't appear to be a homeroom. Use the store record, or fetch
      // it. The tracked fetchedHomeroom shows it when the fetch completes.
      homeroom = this.fetchedHomeroom;
      if (homeroom?.id !== this.classId) {
        homeroom = this.store.peekRecord('group', this.classId);
      }
      if (!homeroom) {
        this.#fetchHomeroom(this.classId);
        return [];
      }
    }

    return [homeroom];
  }

  // fetchedHomeroom is the group that #fetchHomeroom loaded.
  @tracked
  fetchedHomeroom = null;

  #homeroomRequestId = null;

  #fetchHomeroom(id) {
    if (this.#homeroomRequestId === id) {
      return;
    }
    this.#homeroomRequestId = id;
    this.store.findRecord('group', id).then((group) => {
      if (this.classId === id) {
        this.fetchedHomeroom = group;
      }
    });
  }

  @computed('team', 'reliever', 'weekly', 'class')
  get title() {
    if (!isEmpty(this.team)) {
      return `${this.team.label} ${
        this.reliever ? 'relievers' : 'daily'
      } class list`;
    }

    return `${
      this.reliever ? 'Reliever' : 'Blank'
    } class list (${this.weekly ? 'weekly' : 'daily'})`;
  }

  get schoolOpen() {
    let date = this.model.dates.find((item) => item.date === this.date);
    return !isEmpty(date) && date.status !== SchoolStatus.CLOSED;
  }

  // sortDir provides the current direction of sorting.
  @tracked
  sortDir = 'asc';

  // sortProp provides the current entity to be used to sort by.
  @tracked
  sortProp = 'lastName';

  @action
  onChangeDate(value) {
    // Restrict to current year.
    if (moment(value).year() === moment().year()) {
      this.date = value;
    }
  }

  @action
  downloadFullXlsx() {
    let sheets = [];
    let sheetNames = [];

    this.homerooms.forEach((homeroom) => {
      let sheetName = uniqueSheetName(
        cleanExcelSheetName(homeroom.get('name')),
        sheetNames,
      );
      sheetNames.push(sheetName);

      sheets.push({
        name: sheetName,
        data: this.getExportData(homeroom),
      });
    });

    let fileName = `${this.title} ${moment().format('YYYY-MM-DD')}.xlsx`;

    this.excel.export(sheets, {
      multiSheet: true,
      fileName: cleanFileName(fileName),
    });
  }

  @action
  print() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let classLists = document.querySelectorAll('.class-list');
    let content = tablePrintCss;

    // CSS for displaying underlines
    content += `<style>
                  [class*="__homeroom-date"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    margin-right: 20px;
                    width: 100px;
                  }
                  [class*="__homeroom-teachers-name"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    margin-right: 20px;
                    width: 200px;
                  }
                  [class*="__signature"] {
                    border-bottom: 1px solid black;
                    display: inline-block;
                    width: 200px;
                  }
                </style>`;

    content += `<style>.table-tools button { display: none; }</style>`;

    classLists.forEach((classList, index) => {
      if (index !== 0) {
        content += `<div style="page-break-before: always"></div>`;
      }

      content += classList.outerHTML;
    });

    printer.document.body.innerHTML = content;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  getExportData(homeroom) {
    let data = [];

    // Class info
    data.push([`${homeroom.get('name')}`]);

    // Column headers
    let headers = ['Last Name', 'First Name', 'Year', 'Medical', 'Permission'];

    if (this.weekly) {
      let periods = ['', '', '', ''];

      this.dates.currentTimetable.days.forEach((day) => {
        headers.push(titleize(day.name));

        day.periods.forEach((period, i) => {
          if (i > 0) {
            headers.push('');
          }

          periods.push(period.shortName);
        });
      });

      data.push(headers, periods);
    } else {
      let timetableDay = this.dates.dayForDate(this.date);

      if (!isEmpty(timetableDay)) {
        timetableDay.periods.forEach((period) => {
          headers.push(this.reliever ? period.shortName : period.longName);
        });
      }

      if (this.reliever) {
        headers.push('Medical Notes');
        headers.push('Comment');
      }

      data.push(headers);
    }

    // Data
    // Sort data
    let sortedLearners = sortBy(
      homeroom.get('currentLearners'),
      `person.${this.sortProp}`,
    );
    sortedLearners =
      this.sortDir === 'asc' ? sortedLearners : sortedLearners.reverse();
    sortedLearners.forEach((learner) => {
      let permission = !!learner.person.get(
        'fields.core:permissionAlert.value',
      );
      let medical =
        !!learner.person.get('fields.core:criticalInfo.value') ||
        !!learner.person.get('fields.core:medicalCondition.value') ||
        !!learner.person.get('fields.core:allergies.value') ||
        !!learner.person.get('fields.custom:medicalNotes.value') ||
        learner.person.get('learner.currentMedicalConditions').length;

      if (EnrolmentStatus.CURRENT === learner.person.enrolmentStatus) {
        let row = [
          `${learner.person.lastName}`,
          `${learner.person.firstName}`,
          `${learner.person.currentYearLevel}`,
          medical ? '**' : '',
          permission ? '***' : '',
        ];

        if (!this.weekly) {
          let timetableDay = this.dates.dayForDate(this.date);

          if (!isEmpty(timetableDay)) {
            timetableDay.periods.forEach(() => {
              row.push('');
            });
          }

          if (this.reliever) {
            row.push(learner.person.get('fields.core:medicalNotes.value'));
          }
        }

        data.push(row);
      }
    });

    return data;
  }
}
